import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { ActivitySubType } from '@frontend/app/types/globalTypes';
import { ApplicationDrawer } from '@frontend/app/containers/Application/ApplicationDrawer';
import { useGetApplicationsByIds, useGetWorkItemsQuery } from '@frontend/app/hooks';
import { CONTENT_APP_ID, FULFILLMENT_APP_ID, TERMS_APP_ID } from '@frontend/app/constants/applicationIds';
import { Task, WorkletSpecUri } from '@frontend/app/containers/Projects/constants';
import { IWorkflowActionParameters } from '@frontend/app/containers/Application/ApplicationContainer';
import { logger } from '@common';
import { toast } from '@frontend/shadcn/hooks/use-toast';
import { useGetOrderById } from '@frontend/applications/ProductFulfillmentApp/hooks/useGetOrderById';
import { useGetCurrencyCode } from '@frontend/applications/ProductFulfillmentApp/hooks/useGetCurrencyCode';
import { ProjectConfigType } from '@frontend/applications/ProductFulfillmentApp/types/globalTypes';
import { getCurrencySymbol } from '@frontend/app/containers/Settings/ProductFulfillment/utils';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@frontend/shadcn/components/ui/sheet';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { ScrollArea } from '@frontend/shadcn/components/ui/scroll-area';
import { UserAvatar } from '@frontend/app/components';
import { BadgeGroup } from '@frontend/app/refresh-components';
import { EllipsisLabel } from '@frontend/app/refresh-components/EllipsisLabel';
import { chain, isString } from 'lodash';
import { useAppContext } from '@frontend/context/AppContext';
import { GetOrderById_order } from '@frontend/applications/ProductFulfillmentApp/queries/types/GetOrderById';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import useConvertHeicToJpg from '../../../ProjectsPage/GroupContentReviewPage/utils/useConvertHeicToJpg';
import { getActivityIcon } from './activityIconUtils';
import {
  ActivityLog,
  ActivityMedia,
  ActivityAction,
  GroupedActivity,
  ActivityGroupingConfig,
  ACTIVITY_GROUPING_CONFIG
} from './constants';

// MediaItem component for rendering images and videos
interface MediaItemProps {
  media: ActivityMedia;
}

const MediaItem: React.FC<MediaItemProps> = ({ media }) => {
  const convertedImageUrl = useConvertHeicToJpg(media.url);

  const handleClick = () => {
    const urlToOpen = media.liveUploadLink || media.url;
    window.open(urlToOpen, '_blank', 'noopener,noreferrer');
  };

  if (media.type.startsWith('image')) {
    if (!convertedImageUrl) {
      return null; // Loading state, could add a skeleton here if needed
    }

    return (
      <div className="flex-shrink-0 cursor-pointer" onClick={handleClick}>
        <img
          src={convertedImageUrl}
          alt="Activity content"
          className="w-16 h-16 rounded-xl object-cover border border-gray-200 hover:opacity-90 transition-opacity"
          loading="lazy"
        />
      </div>
    );
  }

  if (media.type.startsWith('video')) {
    return (
      <div className="flex-shrink-0 cursor-pointer" onClick={handleClick}>
        <video
          src={media.url}
          className="w-16 h-16 rounded-xl object-cover border border-gray-200 hover:opacity-90 transition-opacity"
          autoPlay
          loop
          muted
          playsInline
        />
      </div>
    );
  }

  return null;
};

// ActionButtons component for rendering activity actions
interface ActionButtonsProps {
  actions: ActivityAction[];
  subType: ActivitySubType;
  activity: ActivityLog;
  onOpenBrief: (action: ActivityAction, activity: ActivityLog, actionId: string) => void;
  onOpenContentReview: (action: ActivityAction, activity: ActivityLog, actionId: string) => void;
  onOpenLivePost: (action: ActivityAction) => void;
  onOpenReviewOrderRequest: (action: ActivityAction, activity: ActivityLog, actionId: string) => void;
  onOpenOrderDetails: (action: ActivityAction) => void;
  loadingActionId: string | null;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ actions, subType, activity, onOpenBrief, onOpenContentReview, onOpenLivePost, onOpenReviewOrderRequest, onOpenOrderDetails, loadingActionId }) => {
  if (actions.length === 0) {
    return null;
  }

  const handleActionClick = (action: ActivityAction, actionIndex: number) => {
    const actionId = `${activity.id}-${actionIndex}`;

    switch (subType) {
      case ActivitySubType.BRIEF_UPDATED: {
        onOpenBrief(action, activity, actionId);
        break;
      }
      case ActivitySubType.CONTENT_SUBMITTED:
      case ActivitySubType.CONTENT_APPROVED:
      case ActivitySubType.CONTENT_REJECTED:
      case ActivitySubType.CONTENT_AUTO_APPROVED: {
        onOpenContentReview(action, activity, actionId);
        break;
      }
      case ActivitySubType.LIVE_POST_UPLOADED: {
        onOpenLivePost(action);
        break;
      }
      case ActivitySubType.CREATOR_ORDER_REQUEST_CREATED: {
        onOpenReviewOrderRequest(action, activity, actionId);
        break;
      }
      case ActivitySubType.CREATOR_ORDER_REQUEST_APPROVED:
      case ActivitySubType.CREATOR_ORDER_REQUEST_REJECTED:
      case ActivitySubType.CREATOR_ORDER_REQUEST_AUTO_APPROVED:
      case ActivitySubType.PRODUCT_SHIPPED: {
        onOpenOrderDetails(action);
        break;
      }
      // Add other cases as needed
    }
  };

  return (
    <div className="flex flex-col gap-1">
      {actions.map((action, index) => {
        const actionId = `${activity.id}-${index}`;
        const isLoading = loadingActionId === actionId;

        return (
          <Button
            key={index}
            variant="link"
            onClick={() => handleActionClick(action, index)}
            className="pl-0"
            size="sm"
            loading={isLoading}
            disabled={isLoading}
          >
            {action.actionText}
          </Button>
        );
      })}
    </div>
  );
};

interface ActivityTimelineProps {
  activities: ActivityLog[];
  projectId: number;
  refetchActivities: () => void;
}

// Format activity time based on createdDate
const formatActivityTime = (date: Date): string => {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Helper functions for order data extraction (based on PFAShipmentsTable.tsx)
const getOrderProducts = (order: GetOrderById_order) => {
  switch (order?.type) {
    case ProjectConfigType.NoCatalogue:
      return order.details?.no_catalogue_line_items || [];
    case ProjectConfigType.Shopify:
    case ProjectConfigType.ProductCatalog:
    default:
      return order.raw?.line_items || [];
  }
};

const getOrderProductsString = (order: GetOrderById_order): string => {
  const products = getOrderProducts(order);

  let productNames: string[] = [];
  switch (order?.type) {
    case ProjectConfigType.NoCatalogue:
      productNames = chain(products)
        .map((lineItem) => lineItem?.productName)
        .filter((n): n is string => isString(n))
        .value();
      break;
    case ProjectConfigType.Shopify:
    case ProjectConfigType.ProductCatalog:
    default:
      productNames = chain(products)
        .map((lineItem) => lineItem?.name)
        .filter((n): n is string => isString(n))
        .value();
      break;
  }

  return productNames.join(', ');
};

interface IShippingLine {
  price?: string;
}

interface IOrderRaw {
  shipping_lines?: IShippingLine[];
}

const calculateOrderShippingCost = (raw: IOrderRaw): string | null => {
  if (!raw?.shipping_lines || raw?.shipping_lines?.length === 0) return null;
  const shippingCost = raw.shipping_lines
    .reduce((total: number, shippingLine: IShippingLine) => {
      const price = parseFloat(shippingLine.price || '0');
      return total + (Number.isNaN(price) ? 0 : price);
    }, 0)
    .toFixed(2);
  return shippingCost;
};

// Get date key for grouping
const getDateKey = (date: Date): string => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Replace placeholders in text
const replacePlaceholders = (text: string, count: number, userName?: string): string => {
  let result = text.replace('{count}', count.toString());
  if (userName) {
    result = result.replace('{userName}', userName);
  }
  return result;
};

// Get grouping key for an activity based on config
const getGroupingKey = (activity: ActivityLog, config: ActivityGroupingConfig): string => {
  switch (config.groupBy) {
    case 'title':
      return activity.title;
    case 'metadata':
      return activity.metadata?.[config.metadataKey || ''] || 'unknown';
    default:
      return 'default';
  }
};

// Create a grouped activity
const createGroupedActivity = (
  activities: ActivityLog[],
  config: ActivityGroupingConfig,
  dateKey: string,
  groupingKey: string
): GroupedActivity => {
  const count = activities.length;
  const earliestDate = activities.reduce((earliest, current) =>
    (current.createdDate < earliest.createdDate ? current : earliest)
  ).createdDate;

  // For title-based grouping, use the actual title (cleaned up)
  let groupTitle = config.groupTitle;
  if (config.groupBy === 'title' && activities.length > 0) {
    const firstTitle = activities[0].title;
    // Remove "Completed" from the end to avoid duplication
    const cleanTitle = firstTitle.replace(/\s+Completed$/, '');
    groupTitle = `${cleanTitle} Completed`;
  }

  // Extract userName from metadata for metadata-based grouping
  let userName: string | undefined;
  if (config.groupBy === 'metadata' && activities.length > 0) {
    // Try to get userName from metadata first, then fallback to groupingKey
    const metadataUserName = activities[0].metadata?.[config.metadataKey || ''];
    userName = metadataUserName || (groupingKey !== 'unknown' ? groupingKey : 'Unknown User');
  }

  return {
    id: `${dateKey}-${activities[0].subType}-${groupingKey}`,
    subType: activities[0].subType,
    type: activities[0].type,
    count,
    activities,
    createdDate: earliestDate,
    title: groupTitle,
    description: replacePlaceholders(config.groupDescription, count, userName),
    groupingKey: config.groupBy,
    groupingValue: groupingKey,
  };
};

// Main grouping function - simple and clear
const groupActivitiesByDate = (activities: ActivityLog[]) => {
  const groups: Record<string, (ActivityLog | GroupedActivity)[]> = {};

  // Step 1: Group by date
  activities.forEach((activity) => {
    const dateKey = getDateKey(activity.createdDate);
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(activity);
  });

  // Step 2: Process each date group
  Object.keys(groups).forEach((dateKey) => {
    const dateActivities = groups[dateKey];
    const result: (ActivityLog | GroupedActivity)[] = [];

    // Group by subtype
    const subtypeGroups: Record<string, ActivityLog[]> = {};

    dateActivities.forEach((activity) => {
      // Only process ActivityLog items, not GroupedActivity items
      if ('count' in activity) {
        result.push(activity);
        return;
      }

      const config = ACTIVITY_GROUPING_CONFIG[activity.subType];

      if (config?.shouldGroup) {
        if (!subtypeGroups[activity.subType]) {
          subtypeGroups[activity.subType] = [];
        }
        subtypeGroups[activity.subType].push(activity);
      } else {
        result.push(activity);
      }
    });

    // Step 3: Process each subtype group
    Object.keys(subtypeGroups).forEach((subType) => {
      const activitiesOfType = subtypeGroups[subType];
      const config = ACTIVITY_GROUPING_CONFIG[subType];
      const minGroupSize = config.minGroupSize || 2;

      if (activitiesOfType.length >= minGroupSize) {
        if (config.groupBy === 'none') {
          // Simple grouping by subtype only
          result.push(createGroupedActivity(activitiesOfType, config, dateKey, 'default'));
        } else {
          // Group by title or metadata
          const subGroups: Record<string, ActivityLog[]> = {};

          activitiesOfType.forEach((activity) => {
            const groupingKey = getGroupingKey(activity, config);
            if (!subGroups[groupingKey]) {
              subGroups[groupingKey] = [];
            }
            subGroups[groupingKey].push(activity);
          });

          // Create grouped activities for each sub-group
          Object.keys(subGroups).forEach((groupingKey) => {
            const groupActivities = subGroups[groupingKey];
            if (groupActivities.length >= minGroupSize) {
              result.push(createGroupedActivity(groupActivities, config, dateKey, groupingKey));
            } else {
              result.push(...groupActivities);
            }
          });
        }
      } else {
        // Not enough activities to group
        result.push(...activitiesOfType);
      }
    });

    // Sort by date (newest first)
    result.sort((a, b) => {
      const aDate = 'count' in a ? a.createdDate : a.createdDate;
      const bDate = 'count' in b ? b.createdDate : b.createdDate;
      return bDate.getTime() - aDate.getTime();
    });

    groups[dateKey] = result;
  });

  return groups;
};

// OrderDetailsDrawer component
interface OrderDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  order: GetOrderById_order;
  loading: boolean;
  currencyCode: string | null;
}

const OrderDetailsDrawer: React.FC<OrderDetailsDrawerProps> = ({
  open,
  onClose,
  order,
  loading,
  currencyCode
}) => {
  if (loading || !order) {
    return (
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent side="right" className="w-[600px] sm:w-[600px]">
          <SheetHeader>
            <SheetTitle>Order Details</SheetTitle>
          </SheetHeader>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="text-gray-500">Loading order details...</div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  const productString = getOrderProductsString(order);
  const shippingCost = calculateOrderShippingCost(order.raw);

  // Handle CSV orders
  const isCSVOrder = order?.artifacts?.isCSVOrder;
  let productItems: string[] = [];

  if (isCSVOrder) {
    const lineItems = order?.details?.line_items || [];
    productItems = lineItems.map((item) => item.variant_title).filter(Boolean);
  } else {
    productItems = productString?.split(', ') || [];
  }

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[600px] sm:w-[600px]">
        <SheetHeader>
          <SheetTitle>Order Details</SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-full mt-6">
          <div className="space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">Creator</h3>
              <div className="flex items-center gap-2">
                <UserAvatar name={order.member?.name || 'Unknown'} />
                <div className="font-medium">{order.member?.name || 'Unknown'}</div>
              </div>
            </div>

            <Separator />

            {/* Order Number */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">Order Number</h3>
              <Badge variant="secondary">
                <EllipsisLabel tooltip={order.raw?.name || 'N/A'} width="100%">
                  {order.raw?.name || 'N/A'}
                </EllipsisLabel>
              </Badge>
            </div>

            {/* Product Cost */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">Product Cost</h3>
              <Badge variant="secondary">
                {order.cost ? `${getCurrencySymbol(currencyCode || 'USD')}${order.cost}` : 'N/A'}
              </Badge>
            </div>

            {/* Shipping Cost */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">Shipping Cost</h3>
              <Badge variant="secondary">
                {shippingCost ? `${getCurrencySymbol(currencyCode || 'USD')}${shippingCost}` : 'N/A'}
              </Badge>
            </div>

            {/* Products Ordered */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">Products Ordered</h3>
              <div className="space-y-2">
                <BadgeGroup items={productItems} />
              </div>
            </div>

            {/* Created On */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">Created On</h3>
              <Badge variant="secondary">
                {order.createdDate ? new Date(order.createdDate).toLocaleDateString() : 'N/A'}
              </Badge>
            </div>

            {/* Fulfillment Status */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">Fulfillment Status</h3>
              <Badge variant="secondary">{order.fulfillmentStatus || 'N/A'}</Badge>
            </div>

            {/* Shipment Status */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">Shipment Status</h3>
              <Badge variant="secondary">{order.shipmentTracking?.shipmentStatus || 'N/A'}</Badge>
            </div>

            {/* Tracking Number */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">Tracking Number</h3>
              <Badge variant="secondary">
                <EllipsisLabel tooltip={order.shipmentTracking?.trackingNumber || 'N/A'} width="100%">
                  {order.shipmentTracking?.trackingNumber || 'N/A'}
                </EllipsisLabel>
              </Badge>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ projectId, activities, refetchActivities }) => {
  const [collapsedDates, setCollapsedDates] = useState<Set<string>>(new Set());

  // State for ApplicationDrawer
  const [showDrawer, setShowDrawer] = useState(false);
  const [deepLinkParameters, setDeepLinkParameters] = useState<Record<string, unknown>>();
  const [appWorkflowParameters, setAppWorkflowParameters] = useState<IWorkflowActionParameters>();
  const [applicationId, setApplicationId] = useState<string>();
  const [memberId, setMemberId] = useState<number | null>(null);

  // State for Order Details Drawer
  const [showOrderDrawer, setShowOrderDrawer] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  // State for action loading
  const [loadingActionId, setLoadingActionId] = useState<string | null>(null);

  // Fetch application data only when needed
  const { data: { applications = [] } = {}, loading: applicationsLoading } = useGetApplicationsByIds(
    [CONTENT_APP_ID, FULFILLMENT_APP_ID, TERMS_APP_ID],
  );
  const application = applications.find((application) => application.id === applicationId);

  const {
    refetchWorkItems,
  } = useGetWorkItemsQuery({
    fetchPolicy: 'network-only',
    skip: true,
  });

  const { pfaApolloClient } = useAppContext();

  // Hooks for order details
  const { currencyCode } = useGetCurrencyCode({
    client: pfaApolloClient,
    skip: !showOrderDrawer || !selectedOrderId,
    fetchPolicy: 'network-only',
  });
  const { order, loading: orderLoading } = useGetOrderById({
    variables: { id: selectedOrderId },
    fetchPolicy: 'network-only',
    skip: !selectedOrderId || !showOrderDrawer,
    client: pfaApolloClient,
  });

  // Handler for opening brief drawer
  const handleOpenBrief = useCallback(async (action: ActivityAction, activity: ActivityLog, actionId: string) => {
    try {
      setLoadingActionId(actionId);

      // Extract brief data from action parameters
      const memberId = activity.memberId;
      const programId = activity.programId;

      const { data: { workItems = [] } = {} } = await refetchWorkItems({
        programId: projectId,
        specUri: WorkletSpecUri.SendTermsWorkletSpecification,
        taskId: Task.ReviewTermsTask,
      });

      const workItem = workItems?.find((item) => item.data?.waiting_for_agreement_task_data?.agreement_id === action.parameters?.agreementId);

      if (!workItem || !memberId || !programId) {
        toast({
          title: 'Missing required brief data, cannot open brief drawer',
          variant: 'destructive',
        });
        logger.error('Missing required brief data:', { workItem, memberId, programId });
        setLoadingActionId(null);
        return;
      }

      setApplicationId(TERMS_APP_ID);

      // Set deep link parameters
      const deepLinkParams = {
        action: 'review_terms',
      };

      setDeepLinkParameters(deepLinkParams);

      // Set workflow parameters
      setAppWorkflowParameters({
        memberIds: [memberId],
        programId,
        workItems: [workItem],
        taskId: Task.ReviewTermsTask,
        workletSpecUri: WorkletSpecUri.SendTermsWorkletSpecification,
        programName: action.parameters?.programName || activity.metadata?.programName || 'Program',
      });

      // Show the drawer
      setShowDrawer(true);
    } catch (error) {
      toast({
        title: 'Failed to open brief drawer',
        variant: 'destructive',
      });
      logger.error('Error opening brief drawer:', error);
    } finally {
      setLoadingActionId(null);
    }
  }, [projectId, refetchWorkItems]);

  const handleOpenContentReview = useCallback(async (action: ActivityAction, activity: ActivityLog) => {
    // Extract brief data from action parameters
    const memberId = activity.memberId;
    const programId = activity.programId;

    if (!memberId || !programId) {
      toast({
        title: 'Missing required content review data, cannot open content review drawer',
        variant: 'destructive',
      });
      logger.error('Missing required content review data:', { memberId, programId });
      return;
    }

    setApplicationId(CONTENT_APP_ID);
    setMemberId(memberId);

    // Set deep link parameters
    const deepLinkParams = {
      context: 'viewContext',
      id: action.parameters?.contentReviewId,
      in_approval_mode: true,
      member_id: memberId,
      program_id: programId,
    };

    setDeepLinkParameters(deepLinkParams);

    // Set workflow parameters
    setAppWorkflowParameters(undefined);

    // Show the drawer
    setShowDrawer(true);
  }, []);

  const handleLivePostClick = useCallback(async (action: ActivityAction) => {
    const liveUploadLink = action.parameters?.liveUploadLink;
    if (liveUploadLink) {
      window.open(liveUploadLink, '_blank', 'noopener,noreferrer');
    } else {
      toast({
        title: 'Missing required live post data, cannot open live post drawer',
        variant: 'destructive',
      });
      logger.error('Missing required live post data:', { liveUploadLink });
    }
  }, []);

  const handleOpenReviewOrderRequest = useCallback(async (action: ActivityAction, activity: ActivityLog, actionId: string) => {
    try {
      setLoadingActionId(actionId);

      // Extract brief data from action parameters
      const memberId = activity.memberId;
      const programId = activity.programId;
      const specUri = action.parameters?.specUri;
      const taskId = {
        [WorkletSpecUri.SendBrandProductCatalogWorkletSpecification]: Task.ReviewOrderRequestTaskV2,
        [WorkletSpecUri.CreatorProductSelectionWorkletSpecification]: Task.ReviewOrderRequestTask,
        [WorkletSpecUri.OfflineCreatorProductSelectionWorkletSpecification]: Task.OfflineReviewOrderRequestTask,
      }[specUri];

      const { data: { workItems = [] } = {} } = await refetchWorkItems({
        programId: projectId,
        specUri,
        taskId,
      });

      const dataAccessKey = {
        [WorkletSpecUri.SendBrandProductCatalogWorkletSpecification]: `${Task.WaitingForOrderRequestTaskV2}_data`,
        [WorkletSpecUri.CreatorProductSelectionWorkletSpecification]: `${Task.WaitingForOrderRequestTask}_data`,
        [WorkletSpecUri.OfflineCreatorProductSelectionWorkletSpecification]: `${Task.OfflineWaitingForOrderRequestTask}_data`,
      }[specUri];

      const workItem = workItems?.find((item) => item.data?.[dataAccessKey]?.orderId === action.parameters?.orderId);

      if (!workItem || !memberId || !programId) {
        toast({
          title: 'Missing required review order request data, cannot open review order request drawer',
          variant: 'destructive',
        });
        logger.error('Missing required review order request data:', { workItem, memberId, programId });
        setLoadingActionId(null);
        return;
      }

      setApplicationId(FULFILLMENT_APP_ID);

      // Set deep link parameters
      const deepLinkParams = {
        action: specUri === WorkletSpecUri.OfflineCreatorProductSelectionWorkletSpecification ? 'offline_review_order_request' : 'review_order_request',
      };

      setDeepLinkParameters(deepLinkParams);

      // Set workflow parameters
      setAppWorkflowParameters({
        memberIds: [memberId],
        programId,
        workItems: [workItem],
        taskId,
        workletSpecUri: specUri,
        programName: action.parameters?.programName || activity.metadata?.programName || 'Program',
      });

      // Show the drawer
      setShowDrawer(true);
    } catch (error) {
      toast({
        title: 'Failed to open review order request drawer',
        variant: 'destructive',
      });
      logger.error('Error opening review order request drawer:', error);
    } finally {
      setLoadingActionId(null);
    }
  }, [projectId, refetchWorkItems]);

  // Handler for opening order details drawer
  const handleOpenOrderDetails = useCallback((action: ActivityAction) => {
    const orderId = action.parameters?.orderId;
    if (!orderId) {
      toast({
        title: 'Missing order ID, cannot open order details',
        variant: 'destructive',
      });
      logger.error('Missing order ID in action parameters:', { action });
      return;
    }

    setSelectedOrderId(orderId);
    setShowOrderDrawer(true);
  }, []);

  const toggleDateCollapse = (dateKey: string) => {
    const newCollapsed = new Set(collapsedDates);
    if (newCollapsed.has(dateKey)) {
      newCollapsed.delete(dateKey);
    } else {
      newCollapsed.add(dateKey);
    }
    setCollapsedDates(newCollapsed);
  };

  const groupedActivities = groupActivitiesByDate(activities);
  const sortedDates = Object.keys(groupedActivities).sort((a, b) => {
    if (a === 'Today') return -1;
    if (a === 'Yesterday') return 0;
    if (b === 'Today') return 1;
    if (b === 'Yesterday') return 0;
    return new Date(b).getTime() - new Date(a).getTime();
  });

  if (sortedDates.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No activities to display
      </div>
    );
  }

  if (applicationsLoading) {
    return (
      <div className="flex items-start gap-3 p-3 border-b border-gray-100 last:border-b-0">
        <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-16" />
          </div>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-3/4" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white flex flex-col justify-start items-start gap-2">
      {sortedDates.map((dateKey) => {
        const dateActivities = groupedActivities[dateKey];
        const isCollapsed = collapsedDates.has(dateKey);

        return (
          <div key={dateKey} className="self-stretch flex flex-col justify-start items-start">
            {/* Date Header */}
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="justify-start text-gray-800 text-sm font-medium font-['Inter'] leading-normal">
                {dateKey}
              </div>
              <button
                onClick={() => toggleDateCollapse(dateKey)}
                className="w-6 h-6 p-1.5 rounded-lg flex justify-center items-center gap-2 hover:bg-gray-100 transition-colors"
              >
                <FontAwesomeIcon
                  icon={faChevronUp}
                  className={`h-3 text-gray-800 transition-transform ${
                    isCollapsed ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </div>

            {/* Activities for this date */}
            {!isCollapsed && (
              <div className="w-full flex flex-col justify-start items-start">
                {dateActivities.map((activity, activityIndex) => {
                  const isLastActivity = activityIndex === dateActivities.length - 1;

                  // Check if this is a grouped activity
                  if ('count' in activity) {
                    // This is a GroupedActivity
                    return (
                      <div key={activity.id} className="w-full flex justify-start items-start gap-2">
                        {/* Icon and connecting line */}
                        <div className="flex-shrink-0 flex flex-col justify-start items-center">
                          {getActivityIcon(activity.subType as ActivitySubType)}
                          {!isLastActivity && (
                            <div className="w-px bg-gray-200" style={{ height: 40 }} />
                          )}
                        </div>

                        {/* Grouped activity content */}
                        <div className="flex-1 pb-6 flex flex-col justify-start items-start min-w-0">
                          <div className="w-full h-6 flex justify-start items-center gap-2">
                            <div className="flex-1 justify-start text-gray-800 text-sm font-medium font-['Inter'] leading-normal truncate">
                              {activity.title}
                            </div>
                            {/* Hide time for grouped activities */}
                          </div>

                          <div className="w-full flex flex-col justify-start items-start gap-1">
                            <div className="w-full justify-start text-gray-500 text-xs font-normal font-['Inter'] leading-tight">
                              {activity.description}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    // This is a regular ActivityLog
                    let height = 40;
                    if (activity.media && activity.media.length > 0) {
                      height += 65;
                    }
                    if (activity.actions && activity.actions.length > 0) {
                      height += 25;
                    }

                    return (
                      <div key={activity.id} className="w-full flex justify-start items-start gap-2">
                        {/* Icon and connecting line */}
                        <div className="flex-shrink-0 flex flex-col justify-start items-center">
                          {getActivityIcon(activity.subType as ActivitySubType)}
                          {!isLastActivity && (
                            <div
                              className="w-px bg-gray-200"
                              style={{
                                height,
                              }}
                            />
                          )}
                        </div>

                        {/* Activity content */}
                        <div className="flex-1 pb-6 flex flex-col justify-start items-start min-w-0">
                          <div className="w-full h-6 flex justify-start items-center gap-2">
                            <div className="flex-1 justify-start text-gray-800 text-sm font-medium font-['Inter'] leading-normal truncate">
                              {activity.title}
                            </div>
                            <div className="flex-shrink-0 text-right justify-start text-gray-500 text-xs font-normal font-['Inter'] leading-tight">
                              {formatActivityTime(activity.createdDate)}
                            </div>
                          </div>

                          <div className="w-full flex flex-col justify-start items-start gap-1">
                            <div className="w-full justify-start text-gray-500 text-xs font-normal font-['Inter'] leading-tight">
                              {activity.description}
                            </div>

                            {/* Media content */}
                            {activity.media && activity.media.length > 0 && (
                              <div className="w-full flex flex-wrap gap-2">
                                {activity.media.map((mediaItem, mediaIndex) => (
                                  <MediaItem key={mediaIndex} media={mediaItem} />
                                ))}
                              </div>
                            )}

                            {/* Action buttons */}
                            <ActionButtons
                              subType={activity.subType as ActivitySubType}
                              actions={activity.actions}
                              activity={activity}
                              onOpenBrief={handleOpenBrief}
                              onOpenContentReview={handleOpenContentReview}
                              onOpenLivePost={handleLivePostClick}
                              onOpenReviewOrderRequest={handleOpenReviewOrderRequest}
                              onOpenOrderDetails={handleOpenOrderDetails}
                              loadingActionId={loadingActionId}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Inline Application Drawer - No routing needed */}
      {showDrawer && application && deepLinkParameters && (
        <ApplicationDrawer
          application={application}
          deepLinkParameters={deepLinkParameters}
          memberId={memberId?.toString()}
          workflowActionParameters={appWorkflowParameters}
          onApplicationClose={() => {
            setShowDrawer(false);
            setDeepLinkParameters(undefined);
            setAppWorkflowParameters(undefined);
            setApplicationId(undefined);
            setMemberId(null);
            refetchActivities();
          }}
        />
      )}

      {/* Order Details Drawer */}
      <OrderDetailsDrawer
        open={showOrderDrawer}
        onClose={() => {
          setShowOrderDrawer(false);
          setSelectedOrderId(null);
        }}
        order={order}
        loading={orderLoading}
        currencyCode={currencyCode}
      />
    </div>
  );
};
